import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../../constants/Constants';
import useAppNavigation from '../../../hooks/useAppNavigation';
import useTimezone from '../../../hooks/useTimezone';
import { AppointmentStatusType } from '../../../proxies/AppointmentProxy';
import type { GetAppointmentDayDetailsResponseAppointmentType } from '../../../redux/ducks/DashboardDucks.types';

type CalendarDayOverviewDayItemProps = {
  data: GetAppointmentDayDetailsResponseAppointmentType;
  yOffset: number;
  height: number;
  index: number;
};

const CalendarDayOverviewDayItem: React.FC<CalendarDayOverviewDayItemProps> = ({
  data,
  yOffset,
  height,
}) => {
  const { i18n } = useTranslation();
  const { navigate, toLocalizedRoute } = useAppNavigation();
  const { currentTimezone } = useTimezone();

  const onAppointmentClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    const route = toLocalizedRoute(
      ROUTES.DASHBOARD_ROUTE_APPOINTMENT_DETAILS.replace(
        ':appointmentId',
        `${data.id}`,
      ),
    );

    navigate(route);
  };

  const heightTW = `min-h-[${height}px] max-h-[${height}px]`;
  const styleHeight = `${height}px`;
  const styleFont = height < 20 ? '8px' : height < 40 ? '12px' : '14px';
  const offsetTop = `${yOffset}px`;

  const statusStyle = useMemo<string>(() => {
    const inPast = data.to < new Date().getTime();

    if (inPast) {
      if (data.status === AppointmentStatusType.Confirmed) {
        return 'bg-amber-50 text-amber-800 border border-amber-800';
      }
    }

    if (data.status === AppointmentStatusType.Confirmed) {
      return 'bg-sky-300 border border-sky-300 border-sky-600';
    }

    if (data.status === AppointmentStatusType.New) {
      return 'bg-white border border-white border-slate-200';
    }

    return 'bg-slate-100 border border-slate-500';
  }, [data]);

  return (
    <span
      className={`absolute flex ${heightTW} min-w-full flex-col`}
      style={{
        height: styleHeight,
        fontSize: styleFont,
        top: offsetTop,
        position: 'absolute',
      }}
    >
      <button
        data-test-id={`dashboard-appointment-item-${data.id}`}
        onClick={onAppointmentClick}
        key={`appointment-day-component-${data.id}`}
        id={`appointment-day-component-${data.id}`}
        aria-label={`appointment-day-component-${data.id}`}
        type="button"
        className={`group flex grow ${statusStyle} ml-[70px] cursor-pointer flex-col items-start justify-center font-bold border border-dotted hover:border-solid hover:shadow`}
      >
        <span className="line-clamp-3 overflow-hidden text-ellipsis break-all px-2 text-start capitalize leading-none tracking-tighter">
          {`${data.client.first_name} ${data.client.last_name}`}
        </span>
      </button>
    </span>
  );
};

export default CalendarDayOverviewDayItem;
